import { Controller } from "@hotwired/stimulus"

// Phase 3.1 Tier 3 — admin: categories CRUD + reorder.
//
// Vanilla HTML5 drag-and-drop (no Sortable.js dependency added). On
// drop we POST the new id-order to the server, which re-stores
// `position` in increments of 10. Inline name editing saves via PATCH
// when admin chooses "Save name" from the kebab. Default-enabled and
// company-override toggles persist on click — no separate save step.
export default class extends Controller {
  static targets = [
    "rows", "row", "nameField",
    "createModal", "deleteModal", "deleteLabel"
  ]
  static values = {
    reorderUrl: String,
    createUrl: String,
    updateUrlTemplate: String,
    destroyUrlTemplate: String,
    overridesUrl: String
  }

  connect() {
    this._showPendingFlash()
  }

  // Surface a toast persisted across the reload that create/delete do.
  _showPendingFlash() {
    let raw
    try {
      raw = sessionStorage.getItem("integration-admin.flash")
      sessionStorage.removeItem("integration-admin.flash")
    } catch (_) { return }
    if (!raw) return
    try {
      const { message, type } = JSON.parse(raw)
      if (message) this._toast(message, type === "alert" ? "alert" : "notice")
    } catch (_) {}
  }

  // ── Drag and drop ─────────────────────────────────────
  onDragStart(event) {
    this._draggingId = event.currentTarget.dataset.id
    event.currentTarget.classList.add("opacity-50")
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = "move"
      event.dataTransfer.setData("text/plain", this._draggingId)
    }
  }

  onDragOver(event) {
    event.preventDefault()
    if (event.dataTransfer) event.dataTransfer.dropEffect = "move"
    const target = event.currentTarget
    if (!this._draggingId || target.dataset.id === this._draggingId) return
    const dragging = this.rowsTarget.querySelector(`[data-id="${this._draggingId}"]`)
    if (!dragging || dragging === target) return
    const rect = target.getBoundingClientRect()
    const before = (event.clientY - rect.top) < (rect.height / 2)
    target.parentNode.insertBefore(dragging, before ? target : target.nextSibling)
  }

  onDrop(event) {
    event.preventDefault()
  }

  onDragEnd(event) {
    event.currentTarget.classList.remove("opacity-50")
    if (!this._draggingId) return
    this._draggingId = null
    this._postReorder()
  }

  async _postReorder() {
    const ids = Array.from(this.rowsTarget.querySelectorAll("[data-id]")).map(el => el.dataset.id)
    const body = new FormData()
    ids.forEach(id => body.append("order[]", id))
    try {
      const res = await fetch(this.reorderUrlValue, {
        method: "POST",
        body,
        credentials: "same-origin",
        headers: { "X-CSRF-Token": this._csrfToken(), "Accept": "application/json" }
      })
      const data = await res.json().catch(() => ({}))
      if (res.ok && data.ok) this._toast("Order updated.", "notice")
      else this._toast(data.message || "Couldn't reorder.", "alert")
    } catch (_) {
      this._toast("Network error — try again.", "alert")
    }
  }

  // ── Inline edits ──────────────────────────────────────
  async saveName(event) {
    const id = event.currentTarget.dataset.id
    const field = this.nameFieldTargets.find(f => f.dataset.id === id)
    if (!field) return
    const url = this.updateUrlTemplateValue.replace("ID", id)
    const body = new FormData()
    body.append("integration_category[name]", field.value)
    await this._patch(url, body, "Name saved.")
  }

  async toggleDefault(event) {
    const btn = event.currentTarget
    const next = btn.dataset.state !== "true"
    const url = this.updateUrlTemplateValue.replace("ID", btn.dataset.id)
    const body = new FormData()
    body.append("integration_category[default_enabled]", next ? "true" : "false")
    const ok = await this._patch(url, body, next ? "Default-on." : "Default-off.")
    if (ok) {
      btn.dataset.state = next ? "true" : "false"
      btn.textContent = next ? "Yes" : "No"
      btn.className = (next
        ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
        : "bg-gray-100 text-gray-600 hover:bg-gray-200") + " inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[12px] font-medium cursor-pointer"
    }
  }

  async toggleCompanyOverride(event) {
    const btn = event.currentTarget
    const next = btn.dataset.state !== "true"
    const body = new FormData()
    body.append("slug", btn.dataset.slug)
    body.append("enabled", next ? "true" : "false")
    try {
      const res = await fetch(this.overridesUrlValue, {
        method: "PATCH",
        body,
        credentials: "same-origin",
        headers: { "X-CSRF-Token": this._csrfToken(), "Accept": "application/json" }
      })
      const data = await res.json().catch(() => ({}))
      if (res.ok && data.ok) {
        this._toast(data.message || "Updated.", "notice")
        btn.dataset.state = next ? "true" : "false"
        btn.innerHTML = `${next
          ? '<svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12Z"/><circle cx="12" cy="12" r="3"/></svg>'
          : '<svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M3 3l18 18M10.6 10.6a3 3 0 0 0 4.2 4.2"/></svg>'} ${next ? "Visible" : "Hidden"}`
        btn.className = (next
          ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
          : "bg-rose-100 text-rose-700 hover:bg-rose-200") + " inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[12px] font-medium cursor-pointer"
      } else {
        this._toast(data.message || "Couldn't update.", "alert")
      }
    } catch (_) {
      this._toast("Network error — try again.", "alert")
    }
  }

  // ── Create modal ──────────────────────────────────────
  openCreate() { this.createModalTarget.classList.remove("hidden") }
  closeCreate() { this.createModalTarget.classList.add("hidden") }

  async submitCreate(event) {
    event.preventDefault()
    const body = new FormData(event.currentTarget)
    try {
      const res = await fetch(this.createUrlValue, {
        method: "POST",
        body,
        credentials: "same-origin",
        headers: { "X-CSRF-Token": this._csrfToken(), "Accept": "application/json" }
      })
      const data = await res.json().catch(() => ({}))
      if (res.ok && data.ok) this._flashAndReload(data.message || "Category added.", "notice")
      else this._toast(data.message || "Couldn't create.", "alert")
    } catch (_) {
      this._toast("Network error — try again.", "alert")
    }
  }

  // ── Delete confirm ────────────────────────────────────
  confirmDelete(event) {
    this._deleteId = event.currentTarget.dataset.id
    if (this.hasDeleteLabelTarget) {
      this.deleteLabelTarget.textContent = event.currentTarget.dataset.name || "this category"
    }
    this.deleteModalTarget.classList.remove("hidden")
  }

  closeDelete() {
    this.deleteModalTarget.classList.add("hidden")
    this._deleteId = null
  }

  async submitDelete() {
    if (!this._deleteId) return
    const url = this.destroyUrlTemplateValue.replace("ID", this._deleteId)
    try {
      const res = await fetch(url, {
        method: "DELETE",
        credentials: "same-origin",
        headers: { "X-CSRF-Token": this._csrfToken(), "Accept": "application/json" }
      })
      const data = await res.json().catch(() => ({}))
      if (res.ok && data.ok) this._flashAndReload(data.message || "Removed.", "notice")
      else this._toast(data.message || "Couldn't remove.", "alert")
    } catch (_) {
      this._toast("Network error — try again.", "alert")
    } finally {
      this.closeDelete()
    }
  }

  stopPropagation(event) { event.stopPropagation() }

  // ── Helpers ───────────────────────────────────────────
  async _patch(url, body, successMessage) {
    try {
      const res = await fetch(url, {
        method: "PATCH",
        body,
        credentials: "same-origin",
        headers: { "X-CSRF-Token": this._csrfToken(), "Accept": "application/json" }
      })
      const data = await res.json().catch(() => ({}))
      if (res.ok && data.ok) {
        this._toast(successMessage || data.message || "Saved.", "notice")
        return true
      }
      this._toast(data.message || "Couldn't save.", "alert")
      return false
    } catch (_) {
      this._toast("Network error — try again.", "alert")
      return false
    }
  }

  _csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ""
  }

  _toast(message, type) {
    const div = document.createElement("div")
    div.className = type === "notice" ? "flash-toast-notice" : "flash-toast-alert"
    div.dataset.controller = "flash"
    div.dataset.flashDismissAfterValue = "5000"
    div.textContent = message
    document.body.prepend(div)
  }

  _flashAndReload(message, type) {
    try {
      sessionStorage.setItem("integration-admin.flash",
        JSON.stringify({ message, type, ts: Date.now() }))
    } catch (_) {}
    window.location.reload()
  }
}
